import { useEffect, useState, useRef } from "react";
import { useQuery } from '@tanstack/react-query';
import { Chart } from 'chart.js/auto';
import './InvoiceChart.css';
import { useVendor } from "../../utils/Context";
import { useNavigate } from "react-router-dom";
import { Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Info } from "lucide-react";
import axios from "axios";

const fetchInvoiceSummary = async ({ queryKey }) => {
    const [_key, year, currency, vendorId] = queryKey;
    const bearerToken = localStorage.getItem("tokenInv");

    let url = `${process.env.REACT_APP_URL}/api/get_monthly_invoice_summery?year=${year}&currency=${currency}`;
    if (vendorId !== -1) {
        url += `&vendor_id=${vendorId}`;
    }

    axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';
    const response = await axios.get(url, {
        headers: {
            'Authorization': `Bearer ${bearerToken}`
        }
    });

    if (response.data.status !== 1) {
        throw new Error(response.data.message || 'Failed to fetch invoice summary.');
    }
    return response.data.data;
};


const getCurrencyCallback = (currency, showSymbol = true) => {
    const currencySymbols = {
        usd: '$',
        gbp: '£',
        inr: '₹',
    };
    const symbol = currencySymbols[currency.toLowerCase()] || "$";

    return (value) => {
        if (showSymbol && symbol) {
            return `${symbol}${value.toLocaleString('en-IN')}`;
        }
        return value.toLocaleString('en-IN');
    };
};

function InvoiceChart() {
    const [selectedYear, setSelectedYear] = useState('all');
    const [selectedCurrency, setSelectedCurrency] = useState('all');
    const { selectedVendor } = useVendor();
    const navigate = useNavigate(); 

    const chartContainer = useRef(null);
    const chartInstance = useRef(null);

    // Pass selectedVendor as a queryKey element
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['invoiceSummary', selectedYear, selectedCurrency, selectedVendor],
        queryFn: fetchInvoiceSummary,
        staleTime: 1000 * 60 * 5,
    });

    useEffect(() => {
        if (!data || !chartContainer.current) return;
        const MONTH_ORDER = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const summary = data.monthly_summary || {};
        const totalAmounts = MONTH_ORDER.map(month => summary[month]?.total_amount || 0);
        const netAmounts = MONTH_ORDER.map(month => summary[month]?.net_amount || 0);
        const taxes = MONTH_ORDER.map(month => summary[month]?.taxes || 0);
        const discounts = MONTH_ORDER.map(month => summary[month]?.discount || 0);
        const invoiceCounts = MONTH_ORDER.map(month => summary[month]?.total_invoice_count || 0);
        const totalDiscrepancies = MONTH_ORDER.map(month => summary[month]?.total_discrepancy || 0);
        const ctx = chartContainer.current.getContext('2d');

        if (chartInstance.current) chartInstance.current.destroy();

        chartInstance.current = new Chart(ctx, {
            data: {
                labels: MONTH_LABELS,
                datasets: [
                    {
                        type: 'bar',
                        label: `Total Amount`,
                        data: totalAmounts,
                        backgroundColor: 'rgba(59, 130, 246, 0.7)', // Blue
                        yAxisID: 'y',
                        barPercentage: 0.6,
                        categoryPercentage: 0.7
                    },
                    {
                        type: 'bar',
                        label: `NET Amount`,
                        data: netAmounts,
                        backgroundColor: 'rgba(139, 92, 246, 0.7)', // Purple
                        yAxisID: 'y',
                        barPercentage: 0.6,
                        categoryPercentage: 0.7
                    },
                    {
                        type: 'bar',
                        label: `Total Discrepancy`,
                        data: totalDiscrepancies,
                        backgroundColor: 'rgba(239, 68, 68, 0.7)',
                        yAxisID: 'y',
                        barPercentage: 0.6,
                        categoryPercentage: 0.7
                    },
                    {
                        type: 'line',
                        label: 'Invoice Count',
                        data: invoiceCounts,
                        borderColor: 'rgba(16, 185, 129, 1)',
                        backgroundColor: 'rgba(16, 185, 129, 0.7)',
                        yAxisID: 'y1',
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                let label = context.dataset.label || '';
                                if (label) label += ': ';
                                if (context.dataset.yAxisID === 'y') {
                                    if (context.dataset.label === 'Total Discrepancy') {
                                        label += getCurrencyCallback('usd')(context.parsed.y);
                                    } else {
                                        label += getCurrencyCallback(selectedCurrency)(context.parsed.y);
                                    }
                                } else {
                                    label += context.parsed.y;
                                }
                                return label;
                            }
                        }
                    }
                },
                 onClick: (evt, elements) => {
                    if (!elements.length) return;
                    const element = elements[0];
                    const datasetIndex = element.datasetIndex;
                    const index = element.index; // ✅ gives the month index (0 = Jan, 1 = Feb, ...)

                    if (chartInstance.current.data.datasets[datasetIndex].label === "Invoice Count") {
                        const month = chartInstance.current.data.labels[index]; // e.g., "Jan"
                        // navigate(`/document?year=${selectedYear}&month=${month}`);
                        // navigate(`/search_for=discrepancy?year=${selectedYear}&month=${month}`);
                        navigate(`/document?tab=invoice&year=${selectedYear}&month=${month}&currency=${selectedCurrency}`);
                    }
                },
                scales: {
                    x: {
                        // removed stacked
                    },
                    y: {
                        // removed stacked
                        type: 'linear',
                        display: true,
                        position: 'left',
                        title: { display: true, text: 'Amount' },
                        ticks: { callback: getCurrencyCallback(selectedCurrency, false) },
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: { display: true, text: 'Invoice Count' },
                        grid: { drawOnChartArea: false },
                        ticks: {
                            precision: 0
                        }
                    },
                    x: {
                        title: { display: true, text: 'Months' },
                    }
                }
            }
        });

    }, [data, selectedCurrency]);

    const totalCount = data ? Object.values(data.monthly_summary || {}).reduce((acc, curr) => acc + (curr.total_invoice_count || 0), 0) : 0;
    const totalAmount = data ? Object.values(data.monthly_summary || {}).reduce((acc, curr) => acc + (curr.total_amount || 0), 0) : 0;
    const totalNetAmount = data ? Object.values(data.monthly_summary || {}).reduce((acc, curr) => acc + (curr.net_amount || 0), 0) : 0;
    const totalDiscrepancy = data ? Object.values(data.monthly_summary || {}).reduce((acc, curr) => acc + (curr.total_discrepancy || 0), 0) : 0;

    return (
        <div className="invoice-chart-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                <h2 className="invoice-chart-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                Baseline Expenditure Profile
                <Tooltip
                    title={
                    <div style={{ padding: '4px', maxWidth: '300px' }}>
                        <div style={{ marginBottom: '6px', fontSize: '12px' }}>
                        Shows total spend (blue bars), total discrepancy (red bars, always USD), and invoice count (green line) per month for the selected year and currency. The spend and discrepancy use the left Y-axis, while invoice count uses the right Y-axis. When 'All' is selected for currency, amounts are summed using the 'Sum Amount USD' column. When a specific currency is selected, only invoices in that currency are included and amounts are summed using the 'Sum Amount' column. Discrepancy is always shown in USD.
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
                        Verification: Go to Document → Invoice and filter by the selected Year. If Currency is 'All', group by month and sum the 'Sum Amount USD' column — this should match each bar. If a specific currency is selected, also filter by that currency and sum the 'Sum Amount' column instead. For the green line, count the number of distinct invoice records per month.
                        </div>
                    </div>
                    }
                    arrow
                    placement="top"
                >
                    <div style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                    <Info size={18} color="#666" />
                    </div>
                </Tooltip>
                </h2>
                
                <div className="invoice-chart-filters" style={{ margin: 0, gap: '12px', display: 'flex' }}>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="year-filter-label" sx={{ fontSize: '0.875rem', top: '-2px' }}>Year</InputLabel>
                        <Select
                            labelId="year-filter-label"
                            id="year-filter"
                            value={selectedYear}
                            label="Year"
                            onChange={(e) => setSelectedYear(e.target.value)}
                            disabled={isLoading}
                            sx={{ height: 36, fontSize: '0.875rem' }}
                        >
                            <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                            {data?.available_years.map(year => (
                                <MenuItem key={year} value={year} sx={{ fontSize: '0.875rem' }}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="currency-filter-label" sx={{ fontSize: '0.875rem', top: '-2px' }}>Currency</InputLabel>
                        <Select
                            labelId="currency-filter-label"
                            id="currency-filter"
                            value={selectedCurrency}
                            label="Currency"
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                            disabled={isLoading}
                            sx={{ height: 36, fontSize: '0.875rem' }}
                        >
                            <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                            {data?.available_currencies.map(currency => (
                                <MenuItem key={currency} value={currency} sx={{ fontSize: '0.875rem' }}>{currency.toUpperCase()}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
            <div className="invoice-chart-canvas-container">
                {isLoading && <p>Loading Chart Data...</p>}
                {isError && <p>Error: {error.message}</p>}
                <canvas ref={chartContainer} style={{ display: isLoading || isError ? 'none' : 'block' }}></canvas>
            </div>
            {!isLoading && !isError && data && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 20px 0', borderTop: '1px solid #e5e7eb', marginTop: '15px', color: '#374151', fontSize: '14px', fontWeight: '600' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>Total Count</span>
                        <span style={{ color: '#10b981' }}>{totalCount}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>Total Amount</span>
                        <span style={{ color: '#3b82f6' }}>{getCurrencyCallback(selectedCurrency)(totalAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>NET Amount</span>
                        <span style={{ color: '#8b5cf6' }}>{getCurrencyCallback(selectedCurrency)(totalNetAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', marginBottom: '4px' }}>Total Discrepancy</span>
                        <span style={{ color: '#ef4444' }}>{getCurrencyCallback('usd')(totalDiscrepancy)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default InvoiceChart;
